/**
 * 小天才 PDF 报告 URL 拼装工具
 *
 *  - 查看用：buildXtcReportUrl(...)        →  /pdf/{kind}?...&token=...
 *  - 导出用：buildXtcReportUrls(...)       →  按 moduleName 切片的 URL 数组（给 puppeteer 后端分页渲染）
 *
 *  报告 kind：
 *    project — 单 Mall（项目报告），4 个 module
 *    mall    — 大圈（商圈报告），3 个 module
 */
import { getCookie, getStorage } from '@lhb/cache';
import { queryObjectToString } from '@lhb/func';

export type XtcReportKind = 'project' | 'mall';

export interface BuildXtcReportUrlParams {
  /** project=modelClusterId, mall=modelCapacityId */
  id: number | string;
  /** 模型配置 id */
  modelConfigId: number | string;
  /** 租户 id */
  tenantId: number | string;
  /** 员工 id */
  employeeId: number | string;
  /** 时间范围（暂留扩展字段） */
  rangeType?: string | number;
  rangeValue?: string | number;
  /** 地图数据类型 */
  mapDataType?: string;
  /** 切片：1..4 (project) | 1..3 (mall)。缺省 → 完整模式（一次性 1 页） */
  moduleName?: number;
}

/**
 * 项目报告 module 总数
 */
export const XTC_PROJECT_MODULE_COUNT = 4;
/**
 * 商圈报告 module 总数
 */
export const XTC_MALL_MODULE_COUNT = 3;

function moduleCount(kind: XtcReportKind): number {
  return kind === 'project' ? XTC_PROJECT_MODULE_COUNT : XTC_MALL_MODULE_COUNT;
}

/**
 * 拼一个 PDF 页面 URL
 * - withToken=true（默认）：用于 window.open 直接打开；URL 含 ?token=...，pdf 页面通过 URL 读取走鉴权
 * - withToken=false：用于异步导出（puppeteer 后端代抓）；后端禁止 URL 带 token，必须省略
 */
export function buildXtcReportUrl(
  kind: XtcReportKind,
  p: BuildXtcReportUrlParams,
  withToken = true,
): string {
  // token 源对齐 request getHeader：flow_token cookie 在部署环境常为 HttpOnly(JS读不到)，回退 getStorage('flowLoginToken')
  const token = withToken ? (getCookie('flow_token') || getStorage('flowLoginToken') || '') : '';
  const qs = queryObjectToString({
    id: p.id,
    modelConfigId: p.modelConfigId,
    tenantId: p.tenantId,
    employeeId: p.employeeId,
    rangeType: p.rangeType ?? 0,
    rangeValue: p.rangeValue ?? 0,
    mapDataType: p.mapDataType || 'youthMapData',
    ...(p.moduleName ? { moduleName: p.moduleName } : {}),
    ...(withToken && token ? { token } : {}),
  });
  return `/pdf/${kind}?${qs}`;
}

/**
 * 按 moduleName 切片产出 URL 数组（puppeteer 后端按 module 分页渲染，再合并 PDF）
 * project: 4 个 URL（m=1..4）；mall: 3 个 URL（m=1..3）
 *
 * 注意：puppeteer 后端禁止 URL 带 token（异步导出接口会校验，返"url不能携带token参数"），
 * 这里强制 withToken=false；后端在自己的服务上下文里走授权。
 */
export function buildXtcReportUrls(kind: XtcReportKind, p: Omit<BuildXtcReportUrlParams, 'moduleName'>): string[] {
  const count = moduleCount(kind);
  return Array.from({ length: count }, (_, i) => buildXtcReportUrl(kind, { ...p, moduleName: i + 1 }, false));
}

/**
 * 本地 dev 强制显示按钮（不依赖后端权限），仅 NODE_TYPE != pe 生效
 * 启动时 XTC_REPORT_BTN_FORCE_SHOW=1 npm run serve:ie
 */
export const XTC_REPORT_BTN_FORCE_SHOW: boolean =
  process.env.XTC_REPORT_BTN_FORCE_SHOW === '1' && process.env.NODE_TYPE !== 'pe';

/**
 * 占位 permission 对象（FORCE_SHOW 模式下返回，方便前端联调）
 */
export function forceShowPerm(event: string, name: string): { event: string; name: string } {
  return { event, name };
}
